// Pure geometry for the pattern visualizer: maps grid events to canvas rects
// and the audio clock to a playhead x. No DOM, no imports beyond plain JS,
// so it is unit-testable. All inputs guarded against NaN, Infinity, and
// degenerate values.

export function loopBeats(grid) {
  const bars = Math.max(1, grid?.numBars ?? 1);
  const beats = grid?.beatsPerBar ?? 4;
  return bars * beats;
}

export function loopSeconds(grid) {
  const beats = loopBeats(grid);
  const bpm = Math.max(1, grid?.bpm ?? 120);
  return (beats * 60) / bpm;
}

export function eventRect(evt, grid, width, height) {
  if (!evt || !grid) {
    return { x: 0, y: 0, w: 2, h: Math.max(1, height || 1) };
  }

  const beats = loopBeats(grid);
  const lanes = Math.max(1, grid.lanes?.length || 1);
  const laneH = (height || 1) / lanes;

  // Guard start: can be astronomically large, negative, or NaN
  let start = evt.start ?? 0;
  if (!Number.isFinite(start)) {
    start = 0;
  }

  // Guard length: clamped to [0, 256] by backend, but we also handle NaN/missing
  let length = evt.length ?? 0.05;
  if (!Number.isFinite(length)) {
    length = 0.05;
  }
  // Enforce minimum visible width
  length = Math.max(0.05, length);

  // Guard lane: can be out of bounds or negative
  let lane = evt.lane ?? 0;
  if (!Number.isFinite(lane)) {
    lane = 0;
  }
  lane = Math.max(0, lane);

  let x = (start / beats) * (width || 1);
  // Guard against Infinity from large start values
  if (!Number.isFinite(x)) {
    x = width || 0;
  }

  let w = Math.max(2, (length / beats) * (width || 1));
  // Guard against Infinity from very large width calculations
  if (!Number.isFinite(w)) {
    w = 2;
  }

  const y = lane * laneH;
  const h = laneH;

  return { x, y, w, h };
}

export function eventStep(evt, grid, stepsPerBeat) {
  // Discrete-grid counterpart to eventRect: instead of a continuous x
  // position, returns which step COLUMN an event quantises to, or null when
  // it doesn't correspond to any real column. null (not a clamped fallback
  // like eventRect's 0) is deliberate here - eventRect always draws
  // SOMEWHERE because any x is harmless on a continuous canvas, but forcing
  // a bad event into step 0 of a discrete grid would light up a real,
  // valid-looking cell that was never actually triggered.
  if (!evt || !grid) {
    return null;
  }

  const spb = Number.isFinite(stepsPerBeat) && stepsPerBeat > 0 ? stepsPerBeat : 4;

  // Guard start: can be missing, NaN, Infinity, negative, or astronomically
  // large but finite (start * spb can itself overflow to Infinity here,
  // same class of hostile input eventRect already guards against).
  const start = evt.start;
  if (!Number.isFinite(start) || start < 0) {
    return null;
  }

  const totalSteps = loopBeats(grid) * spb;
  const step = Math.floor(start * spb);

  if (!Number.isFinite(step) || step < 0 || step >= totalSteps) {
    return null;
  }

  return step;
}

export function playheadX(currentTime, grid, width) {
  if (!grid) {
    return 0;
  }

  const loop = loopSeconds(grid);

  // Guard currentTime: can be negative, NaN, or Infinity
  let t = currentTime ?? 0;
  if (!Number.isFinite(t)) {
    return 0;
  }

  // Degenerate case: zero or negative loop
  if (loop <= 0) {
    return 0;
  }

  // Wrap time to [0, loop)
  const wrapped = ((t % loop) + loop) % loop; // handles negative t
  const normalized = wrapped / loop;

  const x = normalized * (width || 0);

  // Final guard: ensure x is finite and within bounds
  return Number.isFinite(x) ? x : 0;
}

// Drop the lanes that play nothing, and renumber the events onto what is left.
//
// A kit routinely ships slots the producer never used — the pattern's own
// "Slot 5" with no sample and no hits. In the plugin those rows are where you
// drop the next sample, so they earn their space; on the site the pattern is
// read-only, and a blank row is just a gap that makes the beat harder to read.
//
// Returns the grid UNCHANGED when nothing would be trimmed, and also when
// nothing would be left (a pattern with no events at all) — an empty grid
// renders as a blank box, which is worse than the row of empties.
//
// Renumbering matters: `evt.lane` is an index into `lanes`, so removing a row
// without remapping would draw every event below it against the wrong sample.
export function withoutEmptyLanes(grid) {
  const lanes = grid?.lanes;
  const events = grid?.events;
  if (!Array.isArray(lanes) || !Array.isArray(events)) return grid;

  const used = new Set();
  for (const evt of events) {
    const i = evt?.lane;
    if (Number.isInteger(i) && i >= 0 && i < lanes.length) used.add(i);
  }
  if (used.size === 0 || used.size === lanes.length) return grid;

  const kept = [];
  for (let i = 0; i < lanes.length; i += 1) if (used.has(i)) kept.push(i);
  const remap = new Map(kept.map((oldIndex, newIndex) => [oldIndex, newIndex]));

  return {
    ...grid,
    lanes: kept.map((i) => lanes[i]),
    // Events on an out-of-range lane are dropped rather than clamped: they
    // point at a row that does not exist, and inventing one would put a hit
    // on a sample the producer never chose.
    events: events
      .filter((evt) => remap.has(evt?.lane))
      .map((evt) => ({ ...evt, lane: remap.get(evt.lane) })),
  };
}
